import React, { useState } from "react";
import { useList } from "@/hooks/useApi";
import { PageHeader, Loading, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const DEPARTMENTS = ["Sampling", "Cutting", "Printing", "Embroidery", "Stitching", "Washing", "Finishing", "Quality Control", "Packing", "Dispatch"];

function Metric({ label, value, tone }) {
  const c = { success: "text-success", warning: "text-warning", danger: "text-destructive", primary: "text-primary" }[tone] || "text-foreground";
  return (
    <Card className="p-4"><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div><div className={`font-num font-bold text-2xl ${c}`}>{value}</div></Card>
  );
}

function DeptPanel({ dept }) {
  const { data, isLoading } = useList(`dept-${dept}`, `/departments/${encodeURIComponent(dept)}/dashboard`);
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <Metric label="Today Target" value={fmt(data?.todays_target)} tone="primary" />
        <Metric label="Today Production" value={fmt(data?.todays_production)} tone="success" />
        <Metric label="Completed" value={fmt(data?.completed_quantity)} />
        <Metric label="Pending" value={fmt(data?.pending_quantity)} tone="warning" />
        <Metric label="Rejected" value={fmt(data?.rejected_quantity)} tone="danger" />
        <Metric label="Efficiency" value={`${fmt(data?.efficiency)}%`} tone="primary" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-border text-sm font-semibold uppercase tracking-wide text-muted-foreground">Orders in Department</div>
          {data?.orders?.length ? (
            <Table><TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Product</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{data.orders.map((o) => (
                <TableRow key={o.id}><TableCell className="font-num text-primary">{o.order_id}</TableCell><TableCell className="text-sm max-w-[180px] truncate">{o.product_name}</TableCell><TableCell><StatusBadge status={o.status} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          ) : <div className="p-6 text-sm text-muted-foreground text-center">No active orders</div>}
        </Card>
        <div className="grid gap-4">
          <Card className="p-4"><div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Workers Assigned ({data?.employees?.length || 0})</div>
            <div className="flex flex-wrap gap-1.5">{(data?.employees || []).slice(0, 20).map((e) => <span key={e.id} className="text-xs px-2 py-1 rounded bg-secondary">{e.name}</span>)}{!data?.employees?.length && <span className="text-sm text-muted-foreground">None</span>}</div>
          </Card>
          <Card className="p-4"><div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Machines ({data?.machines?.length || 0})</div>
            <div className="flex flex-wrap gap-1.5">{(data?.machines || []).map((m) => <span key={m.id} className="text-xs px-2 py-1 rounded bg-secondary flex items-center gap-1">{m.name}<StatusBadge status={m.status} /></span>)}{!data?.machines?.length && <span className="text-sm text-muted-foreground">None</span>}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function Departments() {
  const [tab, setTab] = useState(DEPARTMENTS[1]);
  return (
    <div>
      <PageHeader title="Department Dashboards" subtitle="Per-department live production overview" testid="departments-header" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4" data-testid="dept-tabs">
          {DEPARTMENTS.map((d) => <TabsTrigger key={d} value={d} data-testid={`dept-tab-${d.toLowerCase().replace(/ /g, "-")}`}>{d}</TabsTrigger>)}
        </TabsList>
        {DEPARTMENTS.map((d) => <TabsContent key={d} value={d}>{tab === d && <DeptPanel dept={d} />}</TabsContent>)}
      </Tabs>
    </div>
  );
}
